/**
 * Record Audio Element Schema
 * Defines the data structure and schema for browser-based audio recording
 */

import type {
    ElementSchema,
    TextFieldSchema,
    NumberFieldSchema,
    BooleanFieldSchema,
    TiptapJSON,
} from '@ai-content-hub/content-kit';

export const defaultRecordAudioElementI18n = {
    en: {
        record: 'Record',
        stop: 'Stop',
        play: 'Play',
        question: 'Question',
        recordAudioHeading: 'Audio recording',
        recordAudioRecordingTime: 'Recording time (seconds)',
        recordAudioSecondsHint: 'seconds (10-300s)',
        recordAudioAutoGrading: 'Auto-grading',
        recordAudioQuestionPlaceholder:
            'Describe the recording task for learners…',
        recordAudioTapToStart: 'Tap to start recording',
        recordAudioStartRecording: 'Start recording',
        recordAudioStopRecording: 'Stop recording',
        recordAudioRerecord: 'Re-record',
        recordAudioMicDenied:
            'Microphone access was denied. Please allow microphone permission.',
        recordAudioMicUnavailable:
            'Could not access microphone. Please check your device settings.',
        recordAudioNoData: 'No recording data',
        recordAudioNoRecordingAvailable: 'No recording available',
    },
    vi: {
        record: 'Ghi âm',
        stop: 'Dừng',
        play: 'Phát',
        question: 'Câu hỏi',
        recordAudioHeading: 'Ghi âm',
        recordAudioRecordingTime: 'Thời gian ghi (giây)',
        recordAudioSecondsHint: 'giây (10-300s)',
        recordAudioAutoGrading: 'Tự động chấm điểm',
        recordAudioQuestionPlaceholder: 'Mô tả nhiệm vụ ghi âm cho học viên…',
        recordAudioTapToStart: 'Chạm để bắt đầu ghi',
        recordAudioStartRecording: 'Bắt đầu ghi',
        recordAudioStopRecording: 'Dừng ghi',
        recordAudioRerecord: 'Ghi lại',
        recordAudioMicDenied:
            'Quyền mic bị từ chối. Vui lòng cho phép truy cập mic.',
        recordAudioMicUnavailable:
            'Không truy cập được mic. Vui lòng kiểm tra thiết bị.',
        recordAudioNoData: 'Không có dữ liệu ghi âm',
        recordAudioNoRecordingAvailable: 'Chưa có bản ghi',
    },
};

export interface RecordAudioElementData {
    /** Recorded audio source (data URL or blob URL) */
    src: string;
    /** Prompt / question shown with the recording task (plain text or rich-text JSON) */
    title: string | TiptapJSON;
    /** Maximum recording duration in seconds */
    maxDuration: number;
    /** When true, learner response is scored automatically (content-type wiring) */
    autoGrading: boolean;
    /** MIME type of the recorded audio */
    mimeType: string;
    /** Duration of the actual recording in seconds */
    duration: number;
}

export const recordAudioElementSchema: ElementSchema<RecordAudioElementData> = {
    type: 'element-record-audio',
    title: 'Record Audio',
    description: 'Record audio directly from the microphone',
    version: '1.0.0',
    category: 'media',
    icon: 'Mic',
    tags: ['audio', 'record', 'microphone', 'voice', 'media', 'sound'],
    fields: [
        {
            name: 'src',
            type: 'text',
            label: 'Audio Source',
            description: 'Recorded audio data (auto-populated)',
            hidden: true,
        } as TextFieldSchema,
        {
            name: 'title',
            type: 'text',
            label: 'Question',
            description: 'Instructions or question for the learner',
            required: true,
            multiline: true,
            placeholder: 'Describe what you see in this picture',
        } as TextFieldSchema,
        {
            name: 'maxDuration',
            type: 'number',
            label: 'Recording Time (seconds)',
            description: 'Maximum recording length',
            defaultValue: 60,
            min: 10,
            max: 300,
            step: 1,
        } as NumberFieldSchema,
        {
            name: 'autoGrading',
            type: 'boolean',
            label: 'Auto-Grading',
            description: 'Enable automatic scoring for this task',
            defaultValue: false,
        } as BooleanFieldSchema,
    ],
    defaultData: {
        src: '',
        title: '',
        maxDuration: 60,
        autoGrading: false,
        mimeType: '',
        duration: 0,
    },
    i18n: defaultRecordAudioElementI18n,
};
